/**
 * Who may cancel and reopen a demand (HS2-EY3F2G) — part of the client↔server contract, so the
 * server routes and the client's visible menu and banner actions apply the same rules.
 *
 * `manager` is a member who may manage any demand: a reviewer or admin. Anyone else reaching these
 * rules is the demand's own requester (the routes refuse everyone else first). A requester may
 * only discard a demand that is still theirs to edit — a `draft`, or one `returned` to them — and
 * may only reopen a demand they could have cancelled, which goes back to that same state. Staff
 * work is never undone by a requester.
 */

/** Whether these roles manage any demand (reviewer or admin), not just their own. */
export function managesDemands(roles: readonly string[]): boolean {
  return roles.some((role) => role === 'reviewer' || role === 'admin');
}

/** The states a requester may cancel their own demand from, and reopen it back into. */
export const REQUESTER_CANCELLABLE_STATUSES = ['draft', 'returned'] as const;
export type RequesterCancellableStatus = (typeof REQUESTER_CANCELLABLE_STATUSES)[number];

function requesterCancellable(status: string | null | undefined): status is RequesterCancellableStatus {
  return (REQUESTER_CANCELLABLE_STATUSES as readonly (string | null | undefined)[]).includes(status);
}

/** Whether the actor may cancel a demand currently in `status`. */
export function canCancelDemand(status: string, manager: boolean): boolean {
  if (status === 'cancelled') return false;
  return manager || requesterCancellable(status);
}

/** Whether the actor may reopen a cancelled demand that was cancelled from `cancelledFrom`. */
export function canReopenDemand(status: string, cancelledFrom: string | null | undefined, manager: boolean): boolean {
  if (status !== 'cancelled') return false;
  return manager || requesterCancellable(cancelledFrom);
}

/**
 * The explicit state a reopened demand returns to: the requester-owned state it was cancelled
 * from, or `null` to derive it from the demand's data (a staff-stage demand).
 */
export function reopenedExplicitStatus(cancelledFrom: string | null | undefined): RequesterCancellableStatus | null {
  return requesterCancellable(cancelledFrom) ? cancelledFrom : null;
}

/** What staff have started on a demand: any of these makes it theirs, not the requester's to recall. */
export interface StaffWork {
  quotationCount: number;
  analysisCount: number;
  decisionCount: number;
}

/**
 * Whether a requester may **withdraw** their submitted demand back to a draft to fix a mistake
 * (HS2-X22G63): only while it is `submitted` and staff have not started on it — no quotation,
 * pricing check, or decision yet. After that they ask staff instead.
 */
export function canWithdrawDemand(status: string, work: StaffWork): boolean {
  return status === 'submitted' && work.quotationCount === 0 && work.analysisCount === 0 && work.decisionCount === 0;
}

/**
 * The statuses a reviewer may **return** a demand to its requester from (HS2-T90PS1): any time
 * before decisions are complete — submitted, or with quotes or a pricing check under way — without
 * rejecting anything. Decided and complete demands are locked (HS2-Y50H8E).
 */
export const RETURNABLE_STATUSES = ['submitted', 'quoting', 'analysed'] as const;

/** Whether a demand in `status` with (or without) a requester can be returned to them. */
export function canReturnDemand(status: string, hasRequester: boolean): boolean {
  return hasRequester && (RETURNABLE_STATUSES as readonly string[]).includes(status);
}

/**
 * Whether the actor may **reopen with an update** a demand whose lines were rejected (HS2-Z6MFSA):
 * the demand is `rejected` (every line) or `closed` with at least one current rejection, and the
 * actor manages demands or is its own requester.
 */
export function canReopenWithUpdate(
  status: string,
  rejectedLineCount: number,
  manager: boolean,
  ownRequester: boolean,
): boolean {
  return (status === 'rejected' || status === 'closed') && rejectedLineCount > 0 && (manager || ownRequester);
}
